import React, {useState, useEffect} from 'react'
import {CardItem} from '../type'
import {
  STATUS,
  VALUE_INACTIVE,
  VALUE_CARD,
  VALUE_CARD_UPPER,
} from '../../../../Utils/Constant/WalletCard'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import './WalletCardMobileCard.scss'
import { formatDateWithUserTimezone } from '../../wallet-card-templates/utils'

interface WalletCardMobileCardProps {
  card: CardItem
  onEdit: (card: CardItem) => void
  onView: (card: CardItem) => void
  onDelete: (card: CardItem) => void
}

export const WalletCardMobileCard: React.FC<WalletCardMobileCardProps> = ({
  card,
  onEdit,
  onView,
  onDelete,
}) => {
  const {t} = useI18n()
  const [showActions, setShowActions] = useState(false)
  const [menuPosition, setMenuPosition] = useState<{top: number; right: number} | null>(null)

  const handleImageClick = () => {
    onView(card)
  }

  const handleActionClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
    const top = rect.bottom + 8
    const right = Math.max(8, window.innerWidth - rect.right - 8)

    setMenuPosition({top, right})
    setShowActions((prev) => !prev)
  }

  return (
    <>
      <div className={`wallet-card-mobile-card ${showActions ? 'dropdown-open' : ''}`}>
        <div className='card-header-section'>
          <div
            className='d-flex align-items-center gap-4 flex-grow-1'
            style={{maxWidth: 'calc(100% - 60px)'}}
          >
            <div className='card-image-container' onClick={handleImageClick}>
              <img src={card.imagePreviewUrl} alt='Card Preview' className='card-image' />
            </div>
            <div className='card-info-section'>
              <h2
                className='card-name cursor-pointer'
                title={card.cardName}
                onClick={() => onView(card)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onView(card)
                  }
                }}
              >
                {card.cardName}
              </h2>
            </div>
          </div>

          <div className='position-relative'>
            <button
              className='btn btn-action-mobile'
              onClick={handleActionClick}
              aria-label='Actions menu'
              aria-expanded={showActions}
            >
              <i className='fas fa-ellipsis-v'></i>
            </button>
          </div>
        </div>

        <div className='d-flex align-items-center justify-content-center gap-3 mb-4'>
          <span className={`card-type-badge ${card.type}`}>
            {card.type
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str) => str.toUpperCase())
              .trim()
              .replace(VALUE_CARD, VALUE_CARD_UPPER)}
          </span>
          <span className={`status-badge ${card.status === STATUS.ACTIVE ? 'active' : 'inactive'}`}>
            {card.status === STATUS.INACTIVE
              ? VALUE_INACTIVE
              : card.status?.charAt(0).toUpperCase() + card.status?.slice(1)}
          </span>
        </div>

        <div className='d-flex align-items-center justify-content-center gap-4 text-sm text-muted'>
          {card.totalScans && (
            <div className='d-flex align-items-center gap-2'>
              <i className='bi bi-graph-up text-muted'></i>
              <span>{card.totalScans}</span>
            </div>
          )}
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-calendar-plus text-muted'></i>
            <span>{formatDateWithUserTimezone(card?.createdAt || '', 'MMM DD, YYYY hh:mm A')}</span>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <i className='bi bi-arrow-clockwise text-muted'></i>
            <span>{formatDateWithUserTimezone(card?.updatedAt || '', 'MMM DD, YYYY hh:mm A')}</span>
          </div>
        </div>
      </div>

      {showActions && (
        <div
          className='actions-dropdown-overlay'
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowActions(false)
          }}
        >
          <div
            className='actions-dropdown-menu'
            style={
              menuPosition
                ? {top: menuPosition?.top, right: menuPosition?.right, transform: 'none'}
                : undefined
            }
          >
            <button
              className='action-item'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onView(card)
                setShowActions(false)
              }}
              title={t('WALLET_CARD.ACTIVE.VIEW_CARD')}
            >
              <i className='fas fa-eye'></i>
            </button>
            <button
              className='action-item'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onEdit(card)
                setShowActions(false)
              }}
              title={t('COMMON.ACTIONS.EDIT')}
            >
              <i className='fas fa-edit'></i>
            </button>
            <button
              className='action-item text-danger'
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onDelete(card)
                setShowActions(false)
              }}
              title={t('COMMON.ACTIONS.DELETE')}
            >
              <i className='fas fa-trash'></i>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
